import { Button, Flex, FormControl, FormLabel, Icon, Input, Select } from "@chakra-ui/react";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import {
  ContactPersonDto,
  ContactSubjectType,
  EmailInviteLanguage,
  useAddContactPersonMutation,
} from "../autogen/bff-api";
import { useApiError } from "./errors/useApiError";
import { isValidEmail } from "./formatting/isValidEmail";
import { PhoneNumberInput } from "./input/PhoneNumberInput";
import { phoneNumberDtoToInput, phoneNumberInputToDto } from "./input/phoneNumberMapping";
import { Explanation } from "./support/Explanation/Explanation";
import { useBaseData } from "./useBaseData";

const initContactPerson = (): ContactPersonDto => ({
  name: "",
  email: "",
  language: EmailInviteLanguage.No,
  title: "",
});

export const ContactPersonForm = ({
  subject,
  onSubmit,
  showFormInitially = false,
  submitLabel,
  openLabel,
}: {
  subject: { subjectType: ContactSubjectType; subjectId: string };
  onSubmit: (newContactPerson: ContactPersonDto) => Promise<void> | void;
  showFormInitially?: boolean;
  submitLabel?: string;
  openLabel?: string;
}) => {
  const baseData = useBaseData();
  const displayer = useApiError();

  const [newContactPerson, setNewContactPerson] = useState<ContactPersonDto>();

  const [addContactPerson, { isLoading: isAdding }] = useAddContactPersonMutation();

  useEffect(() => {
    if (showFormInitially) setNewContactPerson(initContactPerson());
  }, [showFormInitially]);

  const canSave = !!newContactPerson && !!newContactPerson.name.trim() && isValidEmail(newContactPerson.email);

  return newContactPerson ? (
    <Flex flexDirection="column" border="1px solid" borderColor="smBorder" rounded="md" p="5" mt="5">
      <form>
        <FormControl>
          <FormLabel fontSize="sm">{t("Name")}</FormLabel>
          <Input
            autoComplete="off"
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={newContactPerson.name}
            onChange={(e) =>
              setNewContactPerson({
                ...newContactPerson,
                name: e.target.value,
              })
            }
          />
        </FormControl>
        <FormControl pt="2">
          <FormLabel fontSize="sm">{t("Title")}</FormLabel>
          <Input
            autoComplete="off"
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={newContactPerson.title ?? undefined}
            onChange={(e) =>
              setNewContactPerson({
                ...newContactPerson,
                title: e.target.value,
              })
            }
          />
        </FormControl>
        <FormControl pt="2">
          <FormLabel fontSize="sm">{t("Email")}</FormLabel>
          <Input
            autoComplete="off"
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            value={newContactPerson.email}
            onChange={(e) =>
              setNewContactPerson({
                ...newContactPerson,
                email: e.target.value,
              })
            }
          />
        </FormControl>
        <FormControl pt="2">
          <FormLabel fontSize="sm">{t("Phone number")}</FormLabel>
          <PhoneNumberInput
            size="sm"
            value={phoneNumberDtoToInput(newContactPerson.phoneNumber, baseData.callingCodes)}
            onChange={(phoneNumber) =>
              setNewContactPerson({ ...newContactPerson, phoneNumber: phoneNumberInputToDto(phoneNumber) })
            }
          />
        </FormControl>
        <FormControl pt="2">
          <FormLabel fontSize="sm">{t("Invitation language")}</FormLabel>
          <Select
            value={newContactPerson.language}
            size="sm"
            rounded="md"
            backgroundColor="smBackground"
            onChange={(e) =>
              setNewContactPerson({
                ...newContactPerson,
                language: e.target.value as EmailInviteLanguage,
              })
            }
          >
            <option value="No">{t("Norwegian")}</option>
            <option value="En">{t("English")}</option>
          </Select>
        </FormControl>
        <Flex columnGap={2} pt="4">
          <Button variant="ghost" size="sm" onClick={() => setNewContactPerson(undefined)}>
            {t("Cancel")}
          </Button>
          <Explanation text={t("Make sure at least email and name is valid")} enabled={!canSave}>
            <Button
              colorScheme="teal"
              size="sm"
              variant="outline"
              rightIcon={<Icon as={FaCheck} />}
              isLoading={isAdding}
              isDisabled={!canSave}
              onClick={async () => {
                await addContactPerson({
                  subjectType: subject.subjectType,
                  subjectId: subject.subjectId,
                  addContactPersonRequest: { contact: newContactPerson },
                })
                  .unwrap()
                  .catch((e) => {
                    displayer.trigger(e);
                    throw e;
                  });
                await onSubmit(newContactPerson);
                setNewContactPerson(undefined);
              }}
            >
              {submitLabel ?? t("Save contact person")}
            </Button>
          </Explanation>
        </Flex>
      </form>
    </Flex>
  ) : (
    <Flex py="5" px="2">
      <Button size="sm" variant="link" colorScheme="teal" onClick={() => setNewContactPerson(initContactPerson())}>
        {openLabel ?? t("Create new contact person")}
      </Button>
    </Flex>
  );
};
